<template>
  <div class="review">
    <p class="meeting-section-intro">
      <template v-if="readonly">
        {{ proposals.length
          ? 'Corrections proposées par l’assistant sur le mot à mot, et ce qui en a été décidé avant la rédaction des livrables.'
          : 'L’assistant n’a proposé aucune correction sur ce mot à mot.' }}
      </template>
      <template v-else-if="proposals.length">
        L’assistant a relevé {{ proposals.length }} passage{{ proposals.length > 1 ? 's' : '' }} douteux.
        Pour chacun, écoutez l’extrait si besoin, puis acceptez la correction, refusez-la ou saisissez la vôtre.
        Rien n’est modifié tant que vous n’avez pas validé. Cochez « Toujours corriger tel quel dans le futur » pour
        qu’une correction s’applique d’office aux prochains enregistrements de ce CSE, le terme corrigé étant aussi
        ajouté au vocabulaire transmis à la transcription.
      </template>
      <template v-else>
        La relecture automatique n’a rien pu proposer sur ce mot à mot. Vous pouvez lancer la rédaction des livrables.
      </template>
    </p>

    <ol v-if="items.length" class="review-list">
      <li
        v-for="(item, position) in items"
        :key="item.proposal.id"
        class="review-item"
        :class="`review-item--${item.state}`"
      >
        <header class="review-item__header">
          <span class="review-item__number">{{ position + 1 }}</span>
          <span class="review-item__change">
            <s>{{ item.proposal.from }}</s>
            <i class="fas fa-long-arrow-alt-right" aria-hidden="true"></i>
            <strong>{{ item.proposal.to }}</strong>
          </span>
          <button
            v-if="recordingUrl && item.segment"
            type="button"
            class="meetings-outline meetings-outline--small review-play"
            :title="playingId === item.proposal.id ? 'Arrêter l’écoute' : 'Écouter le passage'"
            @click="togglePlay(item)"
          >
            <i :class="playingId === item.proposal.id ? 'fas fa-stop' : 'fas fa-play'" aria-hidden="true"></i>
            {{ formatTimecode(item.segment.start) }}
          </button>
        </header>

        <p v-if="item.proposal.reason" class="review-item__reason">{{ item.proposal.reason }}</p>

        <div class="review-context">
          <p v-if="item.previous" class="review-turn review-turn--muted">
            <span v-if="item.previous.speaker" class="review-turn__speaker">{{ item.previous.speaker }}</span>
            <span>{{ tail(item.previous.text, 180) }}</span>
          </p>
          <p class="review-turn review-turn--current">
            <span v-if="item.turn && item.turn.speaker" class="review-turn__speaker">{{ item.turn.speaker }}</span>
            <span>{{ item.before }}<mark v-if="item.hit" class="review-hit">{{ item.hit }}</mark>{{ item.after }}</span>
          </p>
          <p v-if="item.next" class="review-turn review-turn--muted">
            <span v-if="item.next.speaker" class="review-turn__speaker">{{ item.next.speaker }}</span>
            <span>{{ head(item.next.text, 180) }}</span>
          </p>
        </div>

        <div v-if="canFixSpeaker(item)" class="review-fix">
          <button
            v-if="fixingProposal !== item.proposal.id"
            type="button"
            class="review-fix__toggle"
            title="Le passage est d’une autre personne que celle indiquée ? Réattribuez la phrase."
            @click="fixingProposal = item.proposal.id"
          >
            <i class="fas fa-user-edit" aria-hidden="true"></i> Corriger le locuteur
          </button>
          <meeting-speaker-fix
            v-else
            :meeting="meeting"
            :first="item.turn.firstIndex"
            :last="item.turn.lastIndex"
            :initial="Number(item.proposal.index)"
            :saving="movingSpeaker"
            @move="$emit('move-speaker', $event)"
            @cancel="fixingProposal = null"
          />
        </div>

        <div v-if="!readonly" class="review-decision">
          <div class="review-decision__buttons" role="group" :aria-label="`Décision sur la proposition ${position + 1}`">
            <button
              type="button"
              class="review-choice review-choice--accept"
              :class="{ 'review-choice--active': item.state === 'accept' }"
              @click="decide(item.proposal.id, 'accept')"
            >
              <i class="fas fa-check" aria-hidden="true"></i> Accepter
            </button>
            <button
              type="button"
              class="review-choice review-choice--reject"
              :class="{ 'review-choice--active': item.state === 'reject' }"
              @click="decide(item.proposal.id, 'reject')"
            >
              <i class="fas fa-times" aria-hidden="true"></i> Refuser
            </button>
            <button
              type="button"
              class="review-choice review-choice--edit"
              :class="{ 'review-choice--active': item.state === 'edit' }"
              @click="decide(item.proposal.id, 'edit')"
            >
              <i class="fas fa-pen" aria-hidden="true"></i> Corriger autrement
            </button>
          </div>
          <div v-if="item.state === 'edit'" class="review-edit" :class="{ 'review-edit--confirmed': isConfirmed(item.proposal.id) }">
            <input
              v-model="decisions[item.proposal.id].to"
              type="text"
              class="review-edit__input"
              maxlength="500"
              :readonly="isConfirmed(item.proposal.id)"
              :aria-label="`Correction à appliquer à la place de « ${item.proposal.from} »`"
              placeholder="Texte à mettre à la place"
              @keydown.enter.prevent="confirmEdit(item.proposal.id)"
            >
            <button
              v-if="!isConfirmed(item.proposal.id)"
              type="button"
              class="review-edit__confirm"
              :disabled="!editText(item.proposal.id)"
              @click="confirmEdit(item.proposal.id)"
            >
              <i class="fas fa-check" aria-hidden="true"></i> Valider le texte
            </button>
            <button v-else type="button" class="review-edit__change" @click="unlockEdit(item.proposal.id)">
              <i class="fas fa-pen" aria-hidden="true"></i> Modifier
            </button>
          </div>
          <label v-if="showGlossaryChoice(item)" class="review-glossary">
            <input v-model="decisions[item.proposal.id].glossary" type="checkbox">
            Toujours corriger tel quel dans le futur
          </label>
          <p
            v-if="item.state !== 'pending'"
            class="review-effect"
            :class="{ 'review-effect--waiting': item.state === 'edit' && !isConfirmed(item.proposal.id) }"
          >
            {{ effectLabel(item) }}
          </p>
        </div>
        <p v-else class="review-outcome" :class="`review-outcome--${item.proposal.status}`">
          {{ outcomeLabel(item.proposal) }}
        </p>
      </li>
    </ol>

    <footer v-if="!readonly" class="review-footer">
      <span v-if="proposals.length" class="review-footer__count">
        {{ decidedCount }} sur {{ proposals.length }} tranchée{{ proposals.length > 1 ? 's' : '' }}
      </span>
      <span v-if="unconfirmedEdits" class="review-footer__warning">
        {{ unconfirmedEdits > 1 ? `${unconfirmedEdits} textes saisis à valider ou à annuler.` : 'Un texte saisi à valider ou à annuler.' }}
      </span>
      <button type="button" class="meetings-action" :disabled="submitting || unconfirmedEdits > 0" @click="submit">
        <i :class="submitting ? 'fas fa-spinner fa-spin' : 'fas fa-feather-alt'" aria-hidden="true"></i>
        {{ submitLabel }}
      </button>
    </footer>

    <details v-if="glossaryChanges.length" class="review-auto">
      <summary>Remplacements automatiques du glossaire ({{ glossaryChanges.length }})</summary>
      <ul>
        <li v-for="(change, index) in glossaryChanges" :key="index">
          <s>{{ change.from }}</s> <i class="fas fa-long-arrow-alt-right" aria-hidden="true"></i> <strong>{{ change.to }}</strong>
        </li>
      </ul>
    </details>

    <audio
      v-if="recordingUrl"
      ref="player"
      :src="recordingUrl"
      preload="metadata"
      @timeupdate="onTimeUpdate"
      @pause="onPause"
      @ended="onPause"
    ></audio>
  </div>
</template>

<script>
import { formatTimecode, speakerParagraphs } from './meetingHelpers'
import recordingPlayback, { PLAY_MARGIN_SECONDS } from './recordingPlayback'
import MeetingSpeakerFix from './MeetingSpeakerFix.vue'

const OUTCOME_LABELS = {
  accepted: 'Acceptée',
  rejected: 'Refusée',
  pending: 'Non tranchée',
  stale: 'Passage introuvable au moment de la décision, ignorée'
}

/**
 * Relecture du mot à mot : les corrections proposées par l'assistant, chacune
 * avec la prise de parole précédente, le passage en cause et la suivante, et
 * l'écoute de l'extrait. L'élu accepte, refuse ou saisit sa correction, décide
 * seul si elle devient une règle de son CSE (case jamais cochée d'avance), puis
 * valide : les décisions partent d'un bloc avec la demande de rédaction.
 * Depuis le contexte d'une proposition, « Corriger le locuteur » déplie la
 * prise de parole en cause (MeetingSpeakerFix) pour réattribuer une phrase ;
 * le parent enregistre (`move-speaker`) et renvoie la réunion mise à jour.
 * En lecture seule (réunion déjà relue ou rédigée), la liste montre ce qui a
 * été décidé.
 */
export default {
  name: 'app-meeting-review',
  components: { MeetingSpeakerFix },
  mixins: [recordingPlayback],
  props: {
    meeting: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
    submitting: { type: Boolean, default: false },
    movingSpeaker: { type: Boolean, default: false }
  },
  data () {
    return {
      decisions: {},
      /** Proposition dont la prise de parole est dépliée pour corriger un locuteur. */
      fixingProposal: null
    }
  },
  computed: {
    segments () {
      return Array.isArray(this.meeting.transcript_segments) ? this.meeting.transcript_segments : []
    },
    proposals () {
      const proposals = Array.isArray(this.meeting.transcript_proposals) ? this.meeting.transcript_proposals : []
      return proposals
        .filter(proposal => proposal && proposal.id !== undefined && proposal.from)
        .slice()
        .sort((a, b) => (Number(a.index) - Number(b.index)) || (Number(a.id) - Number(b.id)))
    },
    recordingUrl () {
      return this.meeting.recording_url || ''
    },
    /** Noms donnés aux voix par le relecteur, par étiquette brute du fournisseur. */
    names () {
      return this.meeting.speaker_names && typeof this.meeting.speaker_names === 'object' ? this.meeting.speaker_names : {}
    },
    glossaryChanges () {
      const corrections = Array.isArray(this.meeting.transcript_corrections) ? this.meeting.transcript_corrections : []
      return corrections.filter(change => change && change.source === 'glossary' && change.from)
    },
    /**
     * Prises de parole avec les positions de leurs segments ; sans locuteurs,
     * chaque segment fait office de prise de parole.
     */
    turns () {
      const paragraphs = speakerParagraphs(this.segments, this.names)
      if (paragraphs.length) {
        return paragraphs
      }
      return this.segments.map((segment, position) => ({
        index: 0,
        speaker: '',
        start: segment ? segment.start : null,
        end: segment ? segment.end : null,
        firstIndex: position,
        lastIndex: position,
        text: String((segment && segment.text) || '').trim()
      }))
    },
    items () {
      return this.proposals.map(proposal => {
        const index = Number(proposal.index)
        const segment = this.segments[index] || null
        const turnPosition = this.turns.findIndex(turn => turn.firstIndex <= index && index <= turn.lastIndex)
        const turn = turnPosition >= 0 ? this.turns[turnPosition] : null
        const context = this.splitAround(turn, index, String(proposal.from || ''))
        return {
          proposal,
          segment,
          turn,
          previous: turnPosition > 0 ? this.turns[turnPosition - 1] : null,
          next: turnPosition >= 0 && turnPosition < this.turns.length - 1 ? this.turns[turnPosition + 1] : null,
          before: context.before,
          hit: context.hit,
          after: context.after,
          state: this.stateOf(proposal)
        }
      })
    },
    /** Décisions prêtes à partir : un texte saisi ne compte qu'une fois validé. */
    decidedCount () {
      return Object.keys(this.decisions).filter(key => this.isSettled(this.decisions[key])).length
    },
    unconfirmedEdits () {
      return Object.keys(this.decisions).filter(key => !this.isSettled(this.decisions[key])).length
    },
    submitLabel () {
      if (!this.decidedCount) {
        return this.proposals.length ? 'Rédiger sans relire' : 'Rédiger les livrables'
      }
      return `Valider ${this.decidedCount} décision${this.decidedCount > 1 ? 's' : ''} et rédiger`
    },
    payload () {
      return Object.keys(this.decisions).filter(key => this.isSettled(this.decisions[key])).map(key => {
        const decision = this.decisions[key]
        const out = { id: Number(key), decision: decision.decision }
        if (decision.decision === 'edit') {
          out.to = String(decision.to || '').trim()
        }
        if (decision.decision !== 'reject') {
          out.glossary = !!decision.glossary
        }
        return out
      })
    }
  },
  watch: {
    'meeting.uuid' () {
      this.decisions = {}
      this.fixingProposal = null
    },
    /** Fiche rechargée (phrase réattribuée, voix nommée) : les prises de parole se recomposent, on replie. */
    meeting () {
      this.fixingProposal = null
    }
  },
  methods: {
    formatTimecode,
    /** Une phrase se réattribue pendant la relecture, quand le mot à mot a des locuteurs. */
    canFixSpeaker (item) {
      return !this.readonly && !!item.turn && !!item.turn.speaker
    },
    /** Une décision est prête quand ce n'est pas un texte saisi, ou que ce texte a été validé. */
    isSettled (decision) {
      return !!decision && (decision.decision !== 'edit' || !!decision.confirmed)
    },
    isConfirmed (id) {
      const decision = this.decisions[id]
      return !!decision && decision.decision === 'edit' && !!decision.confirmed
    },
    editText (id) {
      const decision = this.decisions[id]
      return decision ? String(decision.to || '').trim() : ''
    },
    confirmEdit (id) {
      const decision = this.decisions[id]
      const text = this.editText(id)
      if (!decision || decision.decision !== 'edit' || !text) {
        return
      }
      this.$set(this.decisions, id, Object.assign({}, decision, { to: text, confirmed: true }))
    },
    unlockEdit (id) {
      const decision = this.decisions[id]
      if (!decision) {
        return
      }
      this.$set(this.decisions, id, Object.assign({}, decision, { confirmed: false }))
    },
    showGlossaryChoice (item) {
      if (!item.proposal.rule_candidate) {
        return false
      }
      return item.state === 'accept' || (item.state === 'edit' && this.isConfirmed(item.proposal.id))
    },
    /** Ce que la décision prise fera au moment de la validation. */
    effectLabel (item) {
      const decision = this.decisions[item.proposal.id]
      if (!decision) {
        return ''
      }
      const from = item.proposal.from
      if (decision.decision === 'reject') {
        return `À la validation, le passage restera « ${from} ».`
      }
      if (decision.decision === 'edit' && !decision.confirmed) {
        return this.editText(item.proposal.id)
          ? `Validez le texte saisi : il remplacera « ${from} » quand vous validerez la relecture.`
          : 'Saisissez le texte à mettre à la place, puis validez-le.'
      }
      const to = decision.decision === 'edit' ? this.editText(item.proposal.id) : item.proposal.to
      let label = `À la validation, « ${from} » sera remplacé par « ${to} » dans le mot à mot.`
      if (decision.glossary && item.proposal.rule_candidate) {
        label += ' La même correction sera appliquée d’office dans le futur.'
      }
      return label
    },
    outcomeLabel (proposal) {
      let label = proposal.status === 'edited'
        ? `Corrigée en « ${proposal.applied || ''} »`
        : (OUTCOME_LABELS[proposal.status] || proposal.status || '')
      if (proposal.glossary) {
        label += ' · ajoutée au glossaire du CSE'
      }
      return label
    },
    stateOf (proposal) {
      if (this.readonly) {
        return proposal.status || 'pending'
      }
      const decision = this.decisions[proposal.id]
      return decision ? decision.decision : 'pending'
    },
    /** Fin d'un texte long, coupée au mot. */
    tail (text, max) {
      const value = String(text || '').trim()
      if (value.length <= max) {
        return value
      }
      return `… ${value.slice(value.length - max).replace(/^\S*\s/, '')}`
    },
    /** Début d'un texte long, coupé au mot. */
    head (text, max) {
      const value = String(text || '').trim()
      if (value.length <= max) {
        return value
      }
      return `${value.slice(0, max).replace(/\s\S*$/, '')} …`
    },
    /**
     * Texte de la prise de parole autour du passage en cause : ce qui précède
     * (fin), le passage lui-même, ce qui suit (début), pour surligner la seule
     * occurrence visée sans afficher trois minutes de monologue.
     */
    splitAround (turn, index, from) {
      const textOf = position => {
        const segment = this.segments[position]
        return segment ? String(segment.text || '').trim() : ''
      }
      const current = textOf(index)
      if (!turn) {
        return { before: '', hit: '', after: this.head(current, 260) }
      }
      let before = ''
      for (let i = turn.firstIndex; i < index; i++) {
        const text = textOf(i)
        if (text) {
          before += `${text} `
        }
      }
      let after = ''
      for (let i = index + 1; i <= turn.lastIndex; i++) {
        const text = textOf(i)
        if (text) {
          after += ` ${text}`
        }
      }
      const position = from ? current.indexOf(from) : -1
      if (position < 0) {
        return { before: this.tail(before, 220), hit: '', after: this.head(current + after, 260) }
      }
      return {
        before: this.tail(before + current.slice(0, position), 220),
        hit: from,
        after: this.head(current.slice(position + from.length) + after, 220)
      }
    },
    decide (id, decision) {
      const current = this.decisions[id]
      if (current && current.decision === decision) {
        this.$delete(this.decisions, id)
        return
      }
      const proposal = this.proposals.find(candidate => candidate.id === id)
      if (!proposal) {
        return
      }
      this.$set(this.decisions, id, {
        decision,
        to: current && current.to !== undefined ? current.to : String(proposal.to || ''),
        glossary: current && current.glossary !== undefined ? current.glossary : false,
        confirmed: false
      })
    },
    submit () {
      if (this.submitting || this.unconfirmedEdits > 0) {
        return
      }
      const pending = this.proposals.length - this.decidedCount
      if (pending <= 0) {
        this.$emit('submit', this.payload)
        return
      }
      const message = this.decidedCount
        ? `${pending} proposition${pending > 1 ? 's' : ''} non tranchée${pending > 1 ? 's' : ''} : le mot à mot restera tel quel à ${pending > 1 ? 'ces endroits' : 'cet endroit'}. Lancer la rédaction ?`
        : `Lancer la rédaction sans relire ${pending > 1 ? `les ${pending} propositions` : 'la proposition'} ? Le mot à mot sera utilisé tel quel.`
      this.$buefy.dialog.confirm({
        title: this.decidedCount ? 'Valider la relecture' : 'Rédiger sans relire',
        message,
        type: 'is-info',
        hasIcon: true,
        icon: 'question-circle',
        cancelText: 'Revenir à la relecture',
        confirmText: 'Lancer la rédaction',
        onConfirm: () => this.$emit('submit', this.payload)
      })
    },
    togglePlay (item) {
      if (!item.segment) {
        return
      }
      if (this.playingId === item.proposal.id) {
        this.stopPlaying()
        return
      }
      const start = Math.max(0, Number(item.segment.start || 0) - PLAY_MARGIN_SECONDS)
      const rawEnd = item.segment.end !== null && item.segment.end !== undefined ? Number(item.segment.end) : Number(item.segment.start || 0) + 8
      this.playRange(item.proposal.id, start, Math.max(rawEnd, Number(item.segment.start || 0) + 1) + PLAY_MARGIN_SECONDS)
    }
  }
}
</script>

<style scoped>
.review-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.review-item {
  padding: 0.9rem 1.1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.65rem;
  background: #ffffff;
}

.review-item--reject,
.review-item--rejected {
  background: #f9fafb;
}

.review-item__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
}

.review-item__number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.6rem;
  height: 1.6rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 600;
}

.review-item__change {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.95rem;
  overflow-wrap: anywhere;
}

.review-item__change s {
  color: #b91c1c;
}

.review-item__change i {
  color: #9ca3af;
  font-size: 0.8rem;
}

.review-item__change strong {
  color: #166534;
  font-weight: 600;
}

.review-play {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
}

.review-item__reason {
  margin: 0.4rem 0 0;
  color: #6b7280;
  font-size: 0.8rem;
}

.review-context {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.7rem;
  padding: 0.7rem 0.9rem;
  border-radius: 0.55rem;
  background: #f9fafb;
  font-size: 0.85rem;
  line-height: 1.55;
}

.review-turn {
  margin: 0;
  overflow-wrap: anywhere;
}

.review-turn--muted {
  color: #6b7280;
}

.review-turn--current {
  color: #111827;
}

.review-turn__speaker {
  margin-right: 0.45rem;
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.review-hit {
  padding: 0 0.15rem;
  border-radius: 3px;
  background: #fde68a;
  color: #111827;
}

.review-fix {
  margin-top: 0.45rem;
}

.review-fix__toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0;
  border: 0;
  background: none;
  color: #2563eb;
  font: inherit;
  font-size: 0.78rem;
  cursor: pointer;
}

.review-fix__toggle:hover {
  text-decoration: underline;
}

.review-fix__toggle:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.review-decision {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem 1rem;
  margin-top: 0.75rem;
}

.review-decision__buttons {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.review-choice {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 2.1rem;
  padding: 0 0.8rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: #ffffff;
  color: #374151;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.review-choice:hover {
  border-color: #9ca3af;
  background: #f9fafb;
}

.review-choice--accept.review-choice--active {
  border-color: #22c55e;
  background: #dcfce7;
  color: #166534;
}

.review-choice--reject.review-choice--active {
  border-color: #9ca3af;
  background: #e5e7eb;
  color: #374151;
}

.review-choice--edit.review-choice--active {
  border-color: #2563eb;
  background: #dbeafe;
  color: #1d4ed8;
}

.review-choice:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

/* Texte saisi : champ + bouton de validation ; une fois validé, champ vert en lecture seule. */
.review-edit {
  display: flex;
  flex: 1 1 100%;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.review-edit__input {
  flex: 1 1 16rem;
  min-width: 0;
  padding: 0.45rem 0.7rem;
  border: 1px solid #2563eb;
  border-radius: 0.5rem;
  font-size: 0.875rem;
}

.review-edit__input:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.review-edit--confirmed .review-edit__input {
  border-color: #22c55e;
  background: #f0fdf4;
  color: #166534;
}

.review-edit__confirm,
.review-edit__change {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 2.1rem;
  padding: 0 0.8rem;
  border: 1px solid #2563eb;
  border-radius: 0.5rem;
  background: #2563eb;
  color: #ffffff;
  font-size: 0.8rem;
  white-space: nowrap;
  cursor: pointer;
}

.review-edit__confirm:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.review-edit__change {
  border-color: #d1d5db;
  background: #ffffff;
  color: #374151;
}

.review-effect--waiting {
  color: #b45309;
}

.review-glossary {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: #4b5563;
  font-size: 0.8rem;
  cursor: pointer;
}

.review-effect {
  flex: 1 1 100%;
  margin: 0;
  color: #4b5563;
  font-size: 0.8rem;
}

.review-outcome {
  margin: 0.6rem 0 0;
  color: #4b5563;
  font-size: 0.8rem;
  font-weight: 600;
}

.review-outcome--accepted,
.review-outcome--edited {
  color: #166534;
}

.review-outcome--rejected {
  color: #6b7280;
}

.review-outcome--pending {
  color: #92400e;
}

.review-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem 1rem;
  margin-top: 1.1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.review-footer__count {
  margin-right: auto;
  color: #4b5563;
  font-size: 0.85rem;
}

.review-footer__warning {
  color: #b45309;
  font-size: 0.8rem;
}

.review-auto {
  margin-top: 1rem;
  color: #6b7280;
  font-size: 0.8rem;
}

.review-auto summary {
  cursor: pointer;
}

.review-auto summary:hover {
  color: #111827;
}

.review-auto ul {
  margin: 0.4rem 0 0;
  padding-left: 1.1rem;
  list-style: disc;
}

.review-auto s {
  color: #b91c1c;
}

.review-auto strong {
  color: #166534;
  font-weight: 600;
}

@media (max-width: 768px) {
  .review-play {
    margin-left: 0;
  }

  .review-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .review-footer__count {
    margin-right: 0;
  }
}
</style>
